import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import {
  AddUserRequest,
  DeleteUserBusinessRequest,
  DeleteUserRequest,
  GetUserListResponse,
  GetUsersListV1UsersGetParams,
} from 'compliance-api/generated';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY } from '@/modules/admin-dashboard/queries/department.ts';

export const USERS_LIST_QUERY_KEY = 'vestra-compliance/my-users';

export const useUsersListQuery = (
  payload: GetUsersListV1UsersGetParams,
  options?: {
    disabled?: boolean;
    cacheKey?: string;
  },
) => {
  const { disabled = false, cacheKey } = options || {};
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetUserListResponse>({
    queryKey: [USERS_LIST_QUERY_KEY, JSON.stringify(data), cacheKey, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getUsersListV1UsersGet({
        ...payload,
        page: typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useDeleteUsersFromDepartmentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: DeleteUserRequest) => {
      const response = await withErrorHandling(api.v1.removeUserV1UsersDelete(payload), {
        loading: 'Loading...',
        success: 'The user has been successfully deleted',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};

export const useAddUserMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: AddUserRequest) => {
      const response = await withErrorHandling(api.v1.addUserV1UsersAddUserPost(payload), {
        loading: 'Loading...',
        success: 'The user has been successfully created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteUsersFromBusinessMutation = ({
  setErrors,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: DeleteUserBusinessRequest) => {
      const response = await withErrorHandling(api.v1.deleteFromBusinessV1BusinessPost(payload), {
        loading: 'Loading...',
        success: 'The user has been successfully deleted from this business',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};
